import { Button } from '@/components/ui/button';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import FormFieldComponent from '@/components/FormFieldComponent';
import { useNavigate, useParams } from 'react-router-dom';
import ToolBar from '@/components/ToolBar';
import AlertBox from '@/components/AlertBox';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { Form } from '@/components/ui/form';
import { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import FormFieldPhone, { getPhoneNumberValue } from '@/components/FormFieldPhone';
import { UserInviteFormSchema } from './InviteCreate';
import { capitalizeEveryWord, capitalizeFirstLetter, displayFullName } from '@/lib/utils';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import Loader from '@/components/Loader';
import { displayError } from '@/lib/error';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import {
  ADDRESS_LINE_VALIDATION,
  EMAIL_ADDRESS_VALIDATION,
  NAME_VALIDATION,
  STATE_VALIDATION,
  ZIP_CODE_VALIDATION,
} from '@/utils/constant';
import { useGetUserById, useRoles, useUpdateUser } from '@/services/userService';
import { useTeamGetAll } from '@/services/teamService';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import FormFieldMultiSelect from '@/components/FormFieldMultiSelect';
import type { Team } from '@/types/team.types';

export const roleDefaultTeams: Record<string, string[]> = {
  ADMIN: ['ADMIN'],
  SERVER: ['PROCESS_SERVER'],
};

export type FormData = z.infer<typeof UserInviteFormSchema>;
const UserUpdate = () => {
  const { setIsDirty } = useContext(NavigationBlockerContext);
  const navigate = useNavigate();
  const { userId } = useParams();
  const [isAlertOpen, setIsAlertOpen] = useState<boolean | undefined>(undefined);
  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState<boolean>(false);
  const [openSupervisorChangeModal, setOpenSupervisorChangeModal] = useState<Team | null>(null);
  const [defaultTeamOptions, setDefaultTeamOptions] = useState<string[]>([]);
  const form = useForm<z.infer<typeof UserInviteFormSchema>>({
    resolver: zodResolver(UserInviteFormSchema),
    mode: 'onBlur',
  });
  const { formState, control, getValues, reset, setValue, watch } = form;
  const { isValid, isDirty } = formState;
  const teamIds = watch('teamIds');
  const roleId = watch('roleId');
  const { data: roles } = useRoles();
  const { data: teams, isFetching: isFetchingTeams } = useTeamGetAll();
  const { data: userData, isLoading, isError } = useGetUserById(userId);
  const watchedStatus = watch('status');
  const roleOptions = useMemo(() => {
    if (roles) {
      return roles
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        .map((role) => ({ label: capitalizeFirstLetter(role.name), value: role.roleId }));
    }
    return [];
  }, [roles, roleId]);

  const setDefaultTeam = (role: string, existingTeams: string[]) => {
    const defaultTeams = roleDefaultTeams[role];
    if (defaultTeams && defaultTeams.length > 0) {
      const matchingTeams = teamsOptions.filter((team) => defaultTeams.includes(team.team.key));

      const currentTeamIds = existingTeams ?? [];
      const newTeamIds = [...currentTeamIds];
      matchingTeams.forEach((team) => {
        if (!newTeamIds.includes(team.value)) {
          newTeamIds.push(team.value);
        }
      });
      setValue('teamIds', newTeamIds);
      setDefaultTeamOptions(matchingTeams.map((team) => team.value));
    } else {
      setDefaultTeamOptions([]);
    }
  };

  useEffect(() => {
    if (userData) {
      const user = userData;
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        roleId: user.roleId,
        status: user.status,
        ...(user.roleId === 'SERVER' && {
          servers: {
            secondaryEmail: user.secondaryEmail,
            phoneNumber: user.phone,
            addressLine1: user.primaryAddress!,
            addressLine2: user.secondaryAddress,
            city: user.city!,
            state: user.state!,
            zipCode: user.zipCode!,
          },
        }),

        teamIds: user.teams.map((team) => team.id),
        supervisors: user.teams
          .filter((team) => team.supervisorId === userId)
          .reduce(
            (acc, team) => {
              acc[team.id] = true;
              return acc;
            },
            {} as Record<string, boolean>
          ),
      });
      setDefaultTeam(
        user.roleId,
        user.teams.map((team) => team.id)
      );
    }
  }, [userData]);
  const { mutate: updateUser, status } = useUpdateUser();

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  const saveHandler = () => {
    const values = getValues();

    const existingTeams = userData?.teams ?? [];
    const updatedTeams = values.teamIds ?? [];

    const existingIds = new Set(existingTeams.map((t) => t.id));
    const updatedIds = new Set(updatedTeams);

    const create = updatedTeams
      .filter((teamId) => !existingIds.has(teamId))
      .map((teamId) => ({
        teamId,
        isSupervisor: values.supervisors?.[teamId] ?? false,
      }));

    const update = existingTeams
      .filter((team) => updatedIds.has(team.id))
      .map((team) => ({
        teamId: team.id,
        isSupervisor: values.supervisors?.[team.id] ?? false,
      }));

    const deleteIds = existingTeams.filter((team) => !updatedIds.has(team.id)).map((team) => team.id);

    const data = {
      ...values,
      ...(values.roleId === 'SERVER' && {
        secondaryEmail: values.servers?.secondaryEmail,
        phone: getPhoneNumberValue(values.servers?.phoneNumber),
        primaryAddress: values.servers?.addressLine1,
        secondaryAddress: values.servers?.addressLine2,
        city: values.servers?.city,
        state: values.servers?.state,
        zipCode: values.servers?.zipCode,
      }),
      teams: {
        create,
        update,
        delete: deleteIds,
      },
    };

    delete data.servers;
    delete data.teamIds;
    delete data.supervisors;

    updateUser(
      {
        userId: userId,
        data: {
          status: data.status!,
          ...data,
        },
      },
      {
        onSuccess: () => {
          navigate('/users');
          toast({
            variant: 'default',
            description: `${data.firstName} ${data.lastName} has been updated.`,
            duration: 3000,
          });
        },
        onError: (e) => {
          setIsAlertOpen(false);
          displayError(e, 5000);
          navigate(`/users/update/${userId}`);
        },
      }
    );
  };

  const teamsOptions = useMemo(() => {
    return (
      teams?.teams.map((team) => ({
        label1: capitalizeEveryWord(team.name),
        value: team.id,
        team: team,
      })) ?? []
    );
  }, [teams]);

  return (
    <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="user">
      <Form {...form}>
        <Loader isLoading={isLoading || isFetchingTeams}>
          <div className="flex flex-col w-full">
            <ToolBar title={`Edit: ${displayFullName(userData)}`} />
            <div className="flex flex-col gap-6">
              <div>
                <div className="grid grid-cols-2 gap-6 text-black">
                  <FormFieldComponent
                    control={control}
                    name="firstName"
                    label="First Name"
                    placeholder="Enter First Name"
                    required
                    {...NAME_VALIDATION}
                  />
                  <FormFieldComponent
                    control={control}
                    name="lastName"
                    label="Last Name"
                    placeholder="Enter Last Name"
                    required
                    {...NAME_VALIDATION}
                  />
                  <FormFieldComponent
                    control={control}
                    name="email"
                    label="Email Address"
                    placeholder="Enter Email Address"
                    required
                    disabled
                    {...EMAIL_ADDRESS_VALIDATION}
                  />
                  <FormFieldSelect
                    name="roleId"
                    label="Role"
                    placeholder="Select Role"
                    options={roleOptions}
                    required
                    onChange={(newRole) => {
                      const oldRole = userData?.roleId;

                      const oldDefaults = oldRole ? roleDefaultTeams[oldRole] ?? [] : [];
                      const newDefaults = newRole ? roleDefaultTeams[newRole] ?? [] : [];

                      const oldDefaultTeamIds = teamsOptions
                        .filter((t) => oldDefaults.includes(t.team.key))
                        .map((t) => t.value);

                      const newDefaultTeamIds = teamsOptions
                        .filter((t) => newDefaults?.includes(t.team.key))
                        .map((t) => t.value);

                      const updated = teamIds?.filter((id) => !oldDefaultTeamIds.includes(id)) ?? [];

                      newDefaultTeamIds.forEach((id) => {
                        if (!updated?.includes(id)) updated.push(id);
                      });
                      setDefaultTeamOptions(newDefaultTeamIds);
                      setValue('teamIds', updated);
                      setValue('servers', undefined);
                    }}
                  />
                  <FormFieldMultiSelect
                    key={roleId}
                    options={teamsOptions}
                    disabled={watchedStatus === 'INACTIVE'}
                    disabledOptions={defaultTeamOptions}
                    name="teamIds"
                    label="Team(s)"
                    placeholder="Select Team(s)"
                    labelToDisplay={{
                      dropdown: 'label1',
                      selectedOption: 'label1',
                    }}
                  />
                </div>
                <div className="grid grid-cols-1">
                  {teamIds?.map((teamId) => {
                    const team = teamsOptions.find((team) => team.value === teamId);
                    return (
                      <FormFieldCheckbox
                        key={teamId}
                        label={`Supervisor - ${team?.label1}`}
                        name={`supervisors.${teamId}`}
                        control={control}
                        onCheckChange={(value) => {
                          const team = teamsOptions.find((team) => team.value === teamId)?.team;
                          if (value && team?.supervisor && team.supervisorId !== userId)
                            setOpenSupervisorChangeModal(team);
                        }}
                      />
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col gap-6">
                {getValues('roleId') === 'SERVER' && (
                  <div className="w-full flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-4 w-[100%]">
                      <FormFieldComponent
                        control={control}
                        name="servers.secondaryEmail"
                        label="Secondary Email Address"
                        placeholder="Enter Secondary Email Address"
                        maxLength={128}
                      />
                      <FormFieldPhone name="servers.phoneNumber" label="Phone Number" defaultCountry="us" required />
                    </div>
                    <FormFieldComponent
                      control={control}
                      name="servers.addressLine1"
                      label="Address Line 1"
                      placeholder="Enter Address Line 1"
                      required
                      {...ADDRESS_LINE_VALIDATION}
                    />
                    <FormFieldComponent
                      control={control}
                      name="servers.addressLine2"
                      label="Address Line 2"
                      placeholder="Enter Apt, suite etc. (Optional)"
                      {...ADDRESS_LINE_VALIDATION}
                    />
                    <div className="grid grid-cols-3 gap-4 w-[100%]">
                      <FormFieldComponent
                        control={control}
                        name="servers.city"
                        label="City"
                        placeholder="City"
                        required
                        maxLength={50}
                      />
                      <FormFieldComponent
                        control={control}
                        name="servers.state"
                        label="State"
                        placeholder="State"
                        required
                        {...STATE_VALIDATION}
                      />
                      <FormFieldComponent
                        control={control}
                        name="servers.zipCode"
                        label="Zip Code"
                        placeholder="Zip Code"
                        required
                        {...ZIP_CODE_VALIDATION}
                      />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 ">
                  <FormFieldSelect
                    name="status"
                    label="Status"
                    placeholder="Select Status"
                    options={[
                      { label: 'Active', value: 'ACTIVE' },
                      { label: 'Inactive', value: 'INACTIVE' },
                    ]}
                    onChange={(value) => {
                      const status = value;
                      if (status === 'INACTIVE') setValue('teamIds', []);
                      else
                        setValue(
                          'teamIds',
                          userData?.teams.map((team) => team.id)
                        );
                    }}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-4 pt-3">
                <AlertBox
                  triggerButton={
                    <Button
                      type="button"
                      variant="outline"
                      className="text-primary w-[75px] h-[36px] py-2 px-4 rounded-md focus:outline-none border-primary hover:bg-[#EDF5FF]"
                      onClick={() => setIsCancelAlertOpen(true)}
                    >
                      Cancel
                    </Button>
                  }
                  title="Are you sure you want to cancel?"
                  description="All of your current changes will be lost."
                  warning={true}
                  cancelBtnLabel="No"
                  confirmBtnLabel="Yes"
                  cancelBtnClick={() => setIsCancelAlertOpen(false)}
                  confirmBtnClick={() => navigate('/users')}
                  open={isCancelAlertOpen}
                />
                <AlertBox
                  open={isAlertOpen}
                  triggerButton={
                    <Button
                      variant="outline"
                      className="text-[#fff] h-[36px] w-[103px] py-2 px-4 rounded-md bg-primary border-primary hover:bg-[#12375C] hover:text-white"
                      disabled={!isValid || !isDirty || status === 'pending'}
                      onClick={() => {
                        setIsAlertOpen(true);
                      }}
                    >
                      Save
                    </Button>
                  }
                  title={`Do you want to update the record?`}
                  description={'This action will overwrite the previous data with the new data entered.'}
                  cancelBtnLabel="Cancel"
                  confirmBtnLabel="Save"
                  isLoading={status === 'pending'}
                  cancelBtnClick={() => setIsAlertOpen(false)}
                  confirmBtnClick={saveHandler}
                />
              </div>
            </div>
          </div>
          {openSupervisorChangeModal && (
            <AlertBox
              title={`Do you want to assign ${getValues('firstName') ?? ''} ${getValues('lastName') ?? ''} as supervisor?`}
              description={`${displayFullName(openSupervisorChangeModal?.supervisor)} is currently the supervisor of ${capitalizeEveryWord(openSupervisorChangeModal?.name)}. If you continue, ${getValues('firstName') ?? ''} ${getValues('lastName') ?? ''} will become the supervisor of ${capitalizeEveryWord(openSupervisorChangeModal?.name)}.`}
              cancelBtnLabel="No"
              confirmBtnLabel="Yes"
              open={!!openSupervisorChangeModal}
              isLoading={status === 'pending'}
              cancelBtnClick={() => {
                setValue(`supervisors.${openSupervisorChangeModal.id}`, false);
                setOpenSupervisorChangeModal(null);
              }}
              confirmBtnClick={() => {
                setValue(`supervisors.${openSupervisorChangeModal.id}`, true);
                setOpenSupervisorChangeModal(null);
              }}
            />
          )}
        </Loader>
      </Form>
    </NotFoundRedirectionWrapper>
  );
};

export default UserUpdate;
